<template>
  <div class="wa-messages-page">
    <v-card class="wa-shell" elevation="1">
      <div class="wa-page-header d-flex align-center px-3">
        <v-toolbar-title>WhatsApp 訊息</v-toolbar-title>
        <v-spacer />
        <v-btn icon :loading="loadingConversations" @click="loadConversations">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>

      <div class="wa-body">
        <aside class="wa-sidebar">
          <div class="wa-sidebar-search">
            <v-text-field
              v-model="conversationFilter"
              dense
              outlined
              hide-details
              clearable
              placeholder="搜尋電話"
              prepend-inner-icon="mdi-magnify"
            />
          </div>
          <v-progress-linear
            v-if="loadingConversations"
            indeterminate
            color="teal"
            height="2"
          />
          <div class="wa-conversation-list">
            <button
              v-for="c in filteredConversations"
              :key="c.phone"
              type="button"
              class="wa-conversation-item"
              :class="{ 'wa-conversation--active': c.phone === selectedPhone }"
              @click="selectConversation(c.phone)"
            >
              <div class="wa-avatar">
                <v-icon color="teal darken-2" small>mdi-account</v-icon>
              </div>
              <div class="wa-conversation-main">
                <div class="wa-conversation-top">
                  <span class="wa-phone">{{ c.phone }}</span>
                  <span class="wa-time">
                    {{ formatTime(c.lastMessage && c.lastMessage.timestamp) }}
                  </span>
                </div>
                <div class="wa-conversation-bottom">
                  <span class="wa-preview">{{ previewText(c.lastMessage) }}</span>
                  <span class="wa-count">{{ c.count }}</span>
                </div>
              </div>
            </button>
            <div
              v-if="!loadingConversations && !filteredConversations.length"
              class="pa-4 text-center grey--text"
            >
              尚無對話
            </div>
          </div>
        </aside>

        <section class="wa-thread">
          <div v-if="!selectedPhone" class="wa-empty">
            <v-icon size="64" color="grey lighten-1">mdi-message-text-outline</v-icon>
            <div class="mt-2 grey--text">選擇左側對話以檢視訊息</div>
          </div>
          <template v-else>
            <div class="wa-thread-header">
              <div class="text-subtitle-1 font-weight-medium">{{ selectedPhone }}</div>
              <div class="caption grey--text">僅供查閱 · 不可傳送</div>
            </div>
            <v-progress-linear
              v-if="loadingThread"
              indeterminate
              color="teal"
              height="2"
            />
            <div ref="threadScroll" class="wa-thread-scroll">
              <div
                v-for="m in threadMessages"
                :key="m._id"
                class="wa-bubble-row"
              >
                <div class="wa-bubble">
                  <div v-if="m.message" class="wa-bubble-text">{{ m.message }}</div>
                  <div
                    v-for="(att, idx) in m.attachments || []"
                    :key="idx"
                    class="wa-attachment mt-1"
                  >
                    <template v-if="att.attachment">
                      <a
                        v-if="isImageAtt(att)"
                        :href="downloadHref(att.attachment)"
                        target="_blank"
                        rel="noopener"
                      >
                        <img
                          :src="thumbHref(att.attachment)"
                          class="wa-thumb"
                          alt=""
                        />
                      </a>
                      <a
                        v-else
                        :href="downloadHref(att.attachment)"
                        target="_blank"
                        rel="noopener"
                        class="wa-file-link"
                      >
                        <v-icon small left>mdi-paperclip</v-icon>
                        {{ att.filename || att.mimeType || '附件' }}
                      </a>
                    </template>
                    <span v-else class="caption grey--text">
                      {{ att.filename || att.mediaId || '媒體（未下載）' }}
                    </span>
                  </div>
                  <div class="wa-bubble-meta">
                    <span class="wa-type">{{ m.type }}</span>
                    <span>{{ formatTime(m.timestamp) }}</span>
                  </div>
                </div>
              </div>
              <div
                v-if="!loadingThread && !threadMessages.length"
                class="pa-6 text-center grey--text"
              >
                此對話尚無訊息
              </div>
            </div>
          </template>
        </section>
      </div>
    </v-card>
  </div>
</template>

<script>
import {
  getWhatsappConversations,
  getWhatsappMessagesByPhone,
} from '@/api/whatsappMessage';
import { attnDownloadHref, attnThumbHref } from '@/services/filters';
import dayjs from 'dayjs';

export default {
  name: 'WhatsappMessagesPage',
  data() {
    return {
      conversations: [],
      conversationFilter: '',
      selectedPhone: null,
      threadMessages: [],
      loadingConversations: false,
      loadingThread: false,
    };
  },
  computed: {
    filteredConversations() {
      const q = (this.conversationFilter || '').trim().toLowerCase();
      if (!q) return this.conversations;
      return this.conversations.filter((c) =>
        String(c.phone || '')
          .toLowerCase()
          .includes(q),
      );
    },
  },
  created() {
    this.loadConversations();
  },
  methods: {
    async loadConversations() {
      this.loadingConversations = true;
      try {
        const data = await getWhatsappConversations({ limit: 100 });
        this.conversations = Array.isArray(data?.docs)
          ? data.docs
          : Array.isArray(data)
            ? data
            : [];
      } catch (e) {
        this.$store &&
          this.$store.commit &&
          this.$store.commit('snackbar/show', {
            msg: (e && e.message) || '載入對話失敗',
            color: 'error',
          });
        this.conversations = [];
      } finally {
        this.loadingConversations = false;
      }
    },
    async selectConversation(phone) {
      this.selectedPhone = phone;
      this.loadingThread = true;
      this.threadMessages = [];
      try {
        const data = await getWhatsappMessagesByPhone(phone, {
          page: 1,
          limit: 200,
          paginate: true,
        });
        this.threadMessages = Array.isArray(data?.docs)
          ? data.docs
          : Array.isArray(data)
            ? data
            : [];
        this.$nextTick(() => this.scrollThreadToEnd());
      } catch (e) {
        this.threadMessages = [];
      } finally {
        this.loadingThread = false;
      }
    },
    scrollThreadToEnd() {
      const el = this.$refs.threadScroll;
      if (el) el.scrollTop = el.scrollHeight;
    },
    previewText(last) {
      if (!last) return '';
      if (last.message) return last.message;
      return `[${last.type || 'message'}]`;
    },
    formatTime(ts) {
      if (!ts) return '';
      return dayjs(ts).format('YYYY-MM-DD HH:mm');
    },
    downloadHref(id) {
      return attnDownloadHref(id);
    },
    thumbHref(id) {
      return attnThumbHref(id, '256');
    },
    isImageAtt(att) {
      const mime = (att && att.mimeType) || '';
      return mime.startsWith('image/');
    },
  },
};
</script>

<style scoped>
.wa-messages-page {
  height: calc(100vh - 120px);
  min-height: 480px;
  padding: 8px;
}
.wa-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.wa-page-header {
  flex: 0 0 auto;
  min-height: 48px;
  border-bottom: 1px solid #e0e0e0;
}
.wa-body {
  flex: 1 1 auto;
  display: flex;
  min-height: 0;
  background: #e5ddd5;
}
.wa-sidebar {
  width: 340px;
  max-width: 40%;
  background: #fff;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  align-self: stretch;
}
.wa-sidebar-search {
  flex: 0 0 auto;
  padding: 8px;
}
.wa-conversation-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: block;
}
.wa-conversation-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin: 0;
  padding: 10px 12px;
  border: 0;
  border-bottom: 1px solid #f0f0f0;
  background: #fff;
  text-align: left;
  cursor: pointer;
}
.wa-conversation-item:hover {
  background: #f7f7f7;
}
.wa-conversation--active {
  background: #e8f5e9 !important;
}
.wa-avatar {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #b2dfdb;
  display: flex;
  align-items: center;
  justify-content: center;
}
.wa-conversation-main {
  flex: 1 1 auto;
  min-width: 0;
}
.wa-conversation-top,
.wa-conversation-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.wa-phone {
  font-weight: 600;
  font-size: 14px;
  color: #222;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.wa-preview {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12px;
  color: #777;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.wa-time {
  flex: 0 0 auto;
  font-size: 11px;
  color: #888;
  white-space: nowrap;
}
.wa-count {
  flex: 0 0 auto;
  min-width: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: #eee;
  font-size: 11px;
  color: #555;
  text-align: center;
  line-height: 18px;
}
.wa-thread {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}
.wa-thread-header {
  background: #f0f2f5;
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
}
.wa-thread-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}
.wa-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #efeae2;
}
.wa-bubble-row {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 8px;
}
.wa-bubble {
  max-width: min(520px, 85%);
  background: #fff;
  border-radius: 8px;
  padding: 8px 10px 4px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.08);
}
.wa-bubble-text {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 14px;
  line-height: 1.4;
}
.wa-bubble-meta {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
  font-size: 11px;
  color: #667;
}
.wa-type {
  text-transform: uppercase;
  opacity: 0.7;
}
.wa-thumb {
  max-width: 220px;
  max-height: 220px;
  border-radius: 4px;
  display: block;
}
.wa-file-link {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  color: #00695c;
}
</style>
